import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';

import { AUTH_ERROR_CODES, type AuthErrorBody } from '@vevents/auth';

import { AuthService } from './auth.service';
import { BiometricAuthService } from './biometric-auth.service';
import { HapticsService } from '@vevents/native-services';

/**
 * MOB-100 Stage 18 — mobile sudo-prompt orchestrator.
 *
 * Mirrors the Stage 15B web `SudoPromptService` so cross-app vocabulary
 * stays in lockstep, but layered for mobile UX:
 *  - Password input is the always-on fallback.
 *  - Biometric (Face ID / fingerprint) shown when the device reports it
 *    as available — tapping the biometric button invokes
 *    `BiometricAuthService.verifyAndGetAssertion()` and sends the
 *    assertion as the sudo credential.
 *  - `429 sudo-locked` puts the modal into lockout mode with friendly
 *    minute copy ("Too many failed attempts. Try again in N minutes.").
 *  - Cancelled prompts resolve `granted=false` so the interceptor
 *    propagates the original 403 to the caller without auto-retrying.
 *
 * The HTTP interceptor calls `requestSudo({ triggeringAction })`,
 * awaits the resulting promise, and only retries the original request
 * on `granted=true`.
 */
export interface SudoPromptRequest {
  triggeringAction?: string;
  /** Set when the server returned 429 — modal stays open in read-only mode. */
  lockoutMessage?: string;
  /** True once biometric availability has been probed. */
  biometricAvailable?: boolean;
}

@Injectable({ providedIn: 'root' })
export class SudoPromptService {
  private readonly auth = inject(AuthService);
  private readonly biometric = inject(BiometricAuthService);
  private readonly haptics = inject(HapticsService);

  private readonly _request = signal<SudoPromptRequest | null>(null);
  readonly request = this._request.asReadonly();

  private pending: {
    promise: Promise<{ granted: boolean }>;
    resolve: (value: { granted: boolean }) => void;
  } | null = null;

  // ---------------------------------------------------------------------
  // Public surface — used by the sudo interceptor and the modal.
  // ---------------------------------------------------------------------

  /** Interceptor entry-point. Piggy-backs concurrent callers. */
  requestSudo(opts: { triggeringAction?: string } = {}): Promise<{ granted: boolean }> {
    if (this.pending) return this.pending.promise;

    let resolve!: (value: { granted: boolean }) => void;
    const promise = new Promise<{ granted: boolean }>((r) => { resolve = r; });
    this.pending = { promise, resolve };

    this._request.set({ triggeringAction: opts.triggeringAction });
    void this.probeBiometric();
    return promise;
  }

  /** Modal binding — invoked by the password form submit handler. */
  async submitPassword(password: string): Promise<void> {
    if (!this._request() || !this.pending) return;
    try {
      await this.auth.sudo({ password }, this._request()?.triggeringAction);
      await this.haptics.notify('success');
      this.resolveAndClose({ granted: true });
    } catch (err) {
      this.handleSubmitError(err);
      throw err;
    }
  }

  /**
   * Modal binding — invoked by the biometric button. Bubbles a thrown
   * "biometric-declined" error so the prompt can fall back to password.
   */
  async submitBiometric(): Promise<void> {
    if (!this._request() || !this.pending) return;
    const assertion = await this.biometric.verifyAndGetAssertion({
      reason: 'Confirm your identity to continue.',
      title: 'Verify identity'
    });
    if (!assertion) {
      await this.haptics.notify('warning');
      throw new Error('biometric-declined');
    }
    try {
      await this.auth.sudo({ biometricAssertion: assertion }, this._request()?.triggeringAction);
      await this.haptics.notify('success');
      this.resolveAndClose({ granted: true });
    } catch (err) {
      this.handleSubmitError(err);
      throw err;
    }
  }

  cancel(): void {
    void this.haptics.impact('light');
    this.resolveAndClose({ granted: false });
  }

  // ---------------------------------------------------------------------
  // Internals
  // ---------------------------------------------------------------------

  private async probeBiometric(): Promise<void> {
    const available = await this.biometric.isAvailable();
    const current = this._request();
    if (!current) return;
    this._request.set({ ...current, biometricAvailable: available });
  }

  private handleSubmitError(err: unknown): void {
    if (!(err instanceof HttpErrorResponse)) return;
    const body = (err.error ?? {}) as Partial<AuthErrorBody>;
    if (err.status === 429 && body.errorCode === AUTH_ERROR_CODES.SudoLocked) {
      const seconds = body.retryAfterSeconds ??
        Number(err.headers?.get('Retry-After')) ?? 3600;
      const minutes = Math.max(1, Math.ceil(seconds / 60));
      const current = this._request() ?? {};
      this._request.set({
        ...current,
        lockoutMessage: `Too many failed attempts. Try again in ${minutes} minute${minutes === 1 ? '' : 's'}.`
      });
      void this.haptics.notify('error');
      // Resolve the in-flight promise so the interceptor stops waiting,
      // but keep the modal open showing the lockout copy.
      this.resolvePending({ granted: false });
    } else if (err.status === 401) {
      void this.haptics.notify('error');
    }
  }

  private resolveAndClose(value: { granted: boolean }): void {
    this.resolvePending(value);
    this._request.set(null);
  }

  private resolvePending(value: { granted: boolean }): void {
    if (this.pending) {
      const p = this.pending;
      this.pending = null;
      p.resolve(value);
    }
  }
}
